import type { Game } from '../core/game';
import type { DieResult, EnemyEffect, ThrowResult } from '../core/types';
import * as ENEMIES_MODULE from '../core/enemies';
import * as ECONOMY_MODULE from '../core/economy';
import * as BALANCE_MODULE from '../core/balance';
import * as ENEMY_DATA_MODULE from '../data/enemies';
import { ENEMY_BY_ID, ZONES, effectLabel as dataEffectLabel, zoneForLevel } from '../data/enemies';
import { currentBossId } from '../core/world';
import { BALANCE } from '../data/balance';
import { onGameEvent } from './gameApi';
import { triggeredSpells, withPrep, withRearguard, type TriggeredSpell } from './spellsApi';

/**
 * Adaptateur « combat » — COMMENTS v0.3.0 §1.2 (formule de dégâts) et §2
 * (écran récapitulatif), plus le calculateur du panneau de debug.
 *
 * Comme `ui/gameApi.ts`, **tout** ce que livre l'agent `src/core` est
 * détecté à l'exécution : `ThrowResult.breakdown`, `state.combat.throws`,
 * `game.expectedThrow()` et `enemyHpAt()` sont optionnels. Sans eux, l'UI
 * affiche « Historique indisponible », masque le calculateur ou retombe sur les
 * formules déjà exportées par `src/core/economy` — jamais d'erreur.
 */

// ───────────────────────────────────────────────────── Détail d'un lancer

/** Décomposition d'un lancer, telle que le cœur la livrera (COMMENTS §1.2.5). */
export interface ThrowBreakdown {
  /** Somme des faces des dés **Maîtres** (d_M). */
  baseMaster: number;
  /** Somme des faces des dés **Basiques** du contenant (d_B). */
  baseBasic: number;
  /** Somme des dégâts plats des arbres (Σ d_F). */
  flat: number;
  /** D_L = baseMaster + baseBasic + flat. */
  DL: number;
  /** D_C — 1 si le lancer n'est pas critique. */
  DC: number;
  /** D_S — 1 si aucune combinaison. */
  DS: number;
  /** M_P — produit des multiplicateurs de l'arbre de prestige. */
  MP: number;
  /** M_T — produit des multiplicateurs de l'arbre de talents. */
  MT: number;
  /** Multiplicateur des affixes du lancer (*Exécutant*…) ; 1 si aucun. */
  affixMult: number;
  total: number;
  /** Bonus critiques **additifs** (Σ d_C) détaillés, dans l'ordre du cœur. */
  critAdditive: number[];
  /** Bonus critiques **multiplicatifs** (Π m_C) détaillés. */
  critMultipliers: number[];
  /** Multiplicateurs d'effets de sorts (Π m_S(a)), sort lui-même exclu. */
  spellEffectMultipliers: number[];
  /**
   * Diviseur du critique de l'ennemi (Mygale ÷2, Arbalétrier ÷3, Vautour ÷4,
   * A27) — 1 sans ennemi `critDivide`. `1` tant que le cœur ne publie pas ce
   * champ.
   */
  critDivisor?: number;
  /** Diviseur des dés **Basiques** (Tortue Épineuse Gigantesque, A31) — 1 sinon. */
  basicDivisor?: number;
  /** Mage Maudit (A100-1) : la part de bonus des sorts est divisée par 2. */
  spellsHalved?: boolean;
  /** Facteur global de *Préparation* (A100-2) ; 1 sans effet. */
  prepMult?: number;
  /** Emplacements retirés du lancer par l'ennemi (Orc Mage — §F). */
  blockedSlots?: number;
  /**
   * Niveaux des dés (A94) : Σ face × (1,5^niveau − 1) des dés Maîtres, déjà
   * comprise dans `DL`. 0 sans dé de niveau.
   */
  dieLevelBonus?: number;
  /** Sorts du lancer, avec les marqueurs *Arrière-garde* (A89) si le cœur les publie ici. */
  spells?: unknown;
  /**
   * A95 (traces pour le récapitulatif ; 1 ou 0 quand sans effet) : facteur
   * global obtenu des deux Grimoires (déjà compris dans `DS`), multiplicateur
   * d'un ennemi brisé (Osseux), facteur des Basiques (Ferreux, compris dans
   * `DL`), Boisé 5 / Du Vide 5 (maximum du lancer précédent) et excédent reporté
   * (Silicium).
   */
  grimoireNumbersMult?: number;
  grimoireFacesMult?: number;
  brokenMult?: number;
  ironBasicMult?: number;
  woodBasicCarry?: number;
  voidCarry?: number;
  overkillCarry?: number;
  /**
   * A98 : Vingt-dicte a doublé les dés Basiques du lancer ; dégâts que la marque
   * de Null a retenus (le lancer n'a rien infligé) ; diviseur d'Armure
   * (1,05^k, 1 sans armure) ; part des faces due au Mult_Corrupted des dés Corrupted.
   */
  basicDoubled?: boolean;
  nullHeld?: number;
  armorDivisor?: number;
  corruptedBonus?: number;
  /**
   * A122 : *Électrification* (Silicium 5, 1 + 1 % par cumul), cumuls du lancer,
   * et bonus d'*ArchiDicer* (1 + 33 % par sort, plafonné) : deux facteurs du
   * produit, 1 quand ils ne jouent pas.
   */
  electrifyMult?: number;
  electrifyStacks?: number;
  archidicerMult?: number;
  /**
   * A123 : facteur du *Croupier* (×0,01 ou ×4, facteur du produit) et facteur
   * d'affixes du *Rat-Ailé* (×1,2, déjà compris dans les affixes) ; 1 sans effet.
   */
  croupierMult?: number;
  affixFactor?: number;
}

/**
 * Un facteur **nommé** du calcul, lu tel quel dans `ThrowResult.breakdown`.
 *
 * La v0.7.0 ajoute des multiplicateurs que l'interface ne connaît pas à
 * l'avance (Larbin ×0,75, *Premiers*, *ENFER*, *Sorcellerie*, *Critique
 * Magique*…), et d'autres suivront. Plutôt qu'une liste en dur qui masquerait
 * en silence tout nouveau facteur, l'interface **affiche tout ce que le cœur
 * publie** : les clés connues reçoivent un libellé français, les autres sont
 * rendues sous leur nom brut.
 */
export interface ExtraFactor {
  /** Nom d'origine, tel que le cœur l'écrit (`minionMult`, `inferno`…). */
  key: string;
  /** Libellé affiché : traduction connue, sinon le nom brut. */
  label: string;
  value: number;
  /**
   * Entre-t-il dans le produit final ? Vrai pour un facteur déclaré dans
   * `breakdown.factors` ou dont le nom annonce un multiplicateur (`…Mult`,
   * `…Multiplier`, `…Factor`) ; faux pour une valeur simplement informative,
   * qui est alors affichée sans être multipliée.
   */
  multiplicative: boolean;
}

/** Champs de `breakdown` déjà portés nommément par `ThrowDetail`. */
const KNOWN_BREAKDOWN_KEYS = new Set([
  'baseMaster',
  'baseBasic',
  'flat',
  'DL',
  'DC',
  'DS',
  'MP',
  'MT',
  'affixMult',
  'total',
  'critAdditive',
  'critMultipliers',
  'spellEffectMultipliers',
  'critDivisor',
  'basicDivisor',
  'spellsHalved',
  'prepMult',
  'blockedSlots',
  // A94 : bonus des niveaux de dés, détaillé sur sa propre ligne.
  'dieLevelBonus',
  'factors',
  // v0.26.0 : faces du tractopelle, détaillées sur leur propre ligne.
  'backhoeBasic',
  // A89 : marqueurs de sorts renforcés (*Arrière-garde*).
  'spells',
  // A95 : traces des Grimoires et des Matériaux, détaillées sur leurs propres lignes.
  'grimoireNumbersMult',
  'grimoireFacesMult',
  'brokenMult',
  'ironBasicMult',
  'woodBasicCarry',
  'voidCarry',
  'overkillCarry',
  // A98 : Vingt-dicte, marque de Null, Armure et dés Corrupted, détaillés sur leurs propres lignes.
  'basicDoubled',
  'nullHeld',
  'nullReleased',
  'armorDivisor',
  'corruptedBonus',
  // A122 : Électrification (Silicium 5) et ArchiDicer, sur leurs propres lignes (facteurs du produit).
  'electrifyMult',
  'electrifyStacks',
  'archidicerMult',
  // A123 : Croupier (facteur du produit, sa propre ligne) et facteur d'affixes du Rat-Ailé (déjà dans les affixes).
  'croupierMult',
  'affixFactor',
]);

/** Clé de comparaison : minuscules, sans accent, sans suffixe de multiplicateur. */
function factorKey(raw: string): string {
  return raw
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]/g, '')
    .replace(/(multiplier|multiplicateur|mult|factor|facteur)$/, '');
}

/** Libellés français des facteurs connus au moment de l'écriture (v0.7.0). */
const FACTOR_LABEL: Record<string, string> = {
  minion: 'Larbin',
  larbin: 'Larbin',
  primes: 'Premiers',
  premiers: 'Premiers',
  inferno: '666 · ENFER',
  enfer: '666 · ENFER',
  sixes: '666 · ENFER',
  sorcery: 'Sorcellerie',
  sorcellerie: 'Sorcellerie',
  magiccrit: 'Critique Magique',
  critmagic: 'Critique Magique',
  critmachine: 'Machine Critique',
  mutilating: 'Mutilant',
  mutilated: 'Mutilant',
  mutilate: 'Mutilant',
  loaded: 'Pipé',
  spellpower: 'Puissance des sorts',
  prep: 'Préparation',
  preparation: 'Préparation',
  container: 'Contenant',
  gold: 'Or',
  // v0.26.0 (COMMENTS v0.25.0, A72).
  dicemaster: 'Dice Master',
  critstreak: 'Criticité',
  spellstreak: 'Flux arcanique',
  backhoebasic: 'Tractopelle (faces, comprises dans D_L)',
};

/** Libellé d'un facteur : traduction connue, sinon le nom brut du cœur. */
export function factorLabel(key: string): string {
  return FACTOR_LABEL[factorKey(key)] ?? key;
}

/** Le nom annonce-t-il un multiplicateur (et non une valeur informative) ? */
function looksMultiplicative(key: string): boolean {
  return /(mult|multiplier|multiplicateur|factor|facteur)$/i.test(key);
}

function makeFactor(key: string, value: number, multiplicative: boolean): ExtraFactor {
  return { key, label: factorLabel(key), value, multiplicative };
}

/**
 * Facteurs nommés d'un `breakdown`, au-delà de ceux que `ThrowDetail` porte
 * déjà. Trois formes sont acceptées, parce que le cœur n'a pas encore tranché :
 * `breakdown.factors` en tableau `{ name, value }`, en dictionnaire
 * `{ nom: valeur }`, et les clés numériques supplémentaires posées à plat.
 */
function collectExtraFactors(b: Record<string, unknown>): ExtraFactor[] {
  const out: ExtraFactor[] = [];
  const declared = b.factors;
  if (Array.isArray(declared)) {
    for (const raw of declared) {
      if (!raw || typeof raw !== 'object') continue;
      const o = raw as Record<string, unknown>;
      const key =
        typeof o.name === 'string'
          ? o.name
          : typeof o.label === 'string'
            ? o.label
            : typeof o.id === 'string'
              ? o.id
              : typeof o.key === 'string'
                ? o.key
                : '';
      const value = num(o.value, num(o.multiplier, num(o.mult, Number.NaN)));
      if (!key || !Number.isFinite(value)) continue;
      out.push(makeFactor(key, value, true));
    }
  } else if (declared && typeof declared === 'object') {
    for (const [key, value] of Object.entries(declared as Record<string, unknown>)) {
      if (typeof value === 'number' && Number.isFinite(value)) out.push(makeFactor(key, value, true));
    }
  }

  for (const [key, value] of Object.entries(b)) {
    if (KNOWN_BREAKDOWN_KEYS.has(key)) continue;
    if (typeof value === 'number' && Number.isFinite(value)) {
      out.push(makeFactor(key, value, looksMultiplicative(key)));
    } else if (Array.isArray(value)) {
      const numbers = numList(value);
      for (let i = 0; i < numbers.length; i++) {
        out.push(makeFactor(numbers.length > 1 ? `${key} ${i + 1}` : key, numbers[i], looksMultiplicative(key)));
      }
    }
  }
  return out;
}

/** Un lancer enregistré dans `state.combat.throws`. */
export interface CombatThrow {
  id: number;
  /** Seconde de combat où le lancer a eu lieu. */
  at: number;
  enemyName: string;
  result: ThrowResult;
  damageDealt: number;
  killed: boolean;
  /** Lancer **esquivé** (A29) : journalisé à 0 dégât, marqué *Esquivé*. */
  dodged?: boolean;
}

/** Journal de combat conservé jusqu'aux écrans de fin (`state.combat`). */
export interface CombatLog {
  kind: 'expedition' | 'invasion';
  throws: CombatThrow[];
  bestThrow: number;
  durationPlayed: number;
  /** A122-15 : dégâts de poison infligés pendant le combat (0 sans le cœur A122). */
  poisonDamage: number;
  /** A122-15 : ennemis tués par le poison, à leur instant (`state.combat.poisonKills`). */
  poisonKills: PoisonKill[];
}

/** Un ennemi tué par le poison (`state.combat.poisonKills`, A122-15). */
export interface PoisonKill {
  at: number;
  enemyName: string;
  boss: boolean;
}

/** `ThrowResult` enrichi par le cœur (champs optionnels tant qu'il n'a pas livré). */
type ExtendedThrow = Omit<ThrowResult, 'breakdown'> & { breakdown?: Partial<ThrowBreakdown> };

function num(v: unknown, fallback = 0): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : fallback;
}

function numList(v: unknown): number[] {
  return Array.isArray(v) ? v.filter((n): n is number => typeof n === 'number' && Number.isFinite(n)) : [];
}

/**
 * Le journal de combat du cœur, ou `null` s'il n'est pas livré.
 *
 * `state.combat.throws` vaut un **nombre** dans la v0.3.x (`CombatSummary`) et
 * un **tableau** à partir de la v0.4.0 : la distinction se fait sur
 * `Array.isArray`, jamais sur la version.
 */
export function combatLog(game: Game, source?: unknown): CombatLog | null {
  // A122-11 : la source peut être le dernier combat (`game.lastCombat()`), pas seulement `state.combat`.
  const raw = (source ?? (game.state as unknown as { combat?: unknown }).combat) as Record<string, unknown> | null | undefined;
  if (!raw || typeof raw !== 'object') return null;
  const throws = raw.throws;
  if (!Array.isArray(throws)) return null;

  const entries: CombatThrow[] = [];
  for (const t of throws as Record<string, unknown>[]) {
    if (!t || typeof t !== 'object') continue;
    const result = t.result as ThrowResult | undefined;
    if (!result || !Array.isArray((result as ThrowResult).dice)) continue;
    entries.push({
      id: num(t.id, entries.length + 1),
      at: num(t.at),
      enemyName: typeof t.enemyName === 'string' ? t.enemyName : '—',
      result,
      damageDealt: num(t.damageDealt, num(result.total)),
      killed: t.killed === true,
      dodged: t.dodged === true,
    });
  }

  const kind = raw.kind === 'invasion' ? 'invasion' : 'expedition';
  const lastAt = entries.length > 0 ? Math.max(...entries.map((e) => e.at)) : 0;
  const duration = num(raw.durationPlayed) || num((game.state as { timeSpent?: number }).timeSpent) || lastAt;
  return {
    kind,
    throws: entries,
    bestThrow: num(raw.bestThrow, entries.reduce((m, e) => Math.max(m, num(e.result.total)), 0)),
    durationPlayed: duration,
    poisonDamage: Math.max(0, num(raw.poisonDamage)),
    poisonKills: poisonKillsOf(raw.poisonKills),
  };
}

function poisonKillsOf(raw: unknown): PoisonKill[] {
  if (!Array.isArray(raw)) return [];
  const out: PoisonKill[] = [];
  for (const k of raw as Record<string, unknown>[]) {
    if (!k || typeof k !== 'object') continue;
    out.push({ at: num(k.at), enemyName: typeof k.enemyName === 'string' ? k.enemyName : '—', boss: k.boss === true });
  }
  return out;
}

/**
 * Le journal du **dernier combat** (A122-11) : `game.lastCombat()` du cœur, à
 * défaut `state.combat` tant qu'il porte des lancers (il reste celui du dernier
 * combat jusqu'au suivant). `null` : aucun combat dans cette session.
 */
export function lastCombatLog(game: Game): CombatLog | null {
  const fn = (game as unknown as { lastCombat?: () => unknown }).lastCombat;
  if (typeof fn === 'function') {
    let last: unknown = null;
    try {
      last = fn.call(game);
    } catch {
      last = null;
    }
    const log = last ? combatLog(game, last) : null;
    if (log && log.throws.length > 0) return log;
    return null;
  }
  const log = combatLog(game);
  return log && log.throws.length > 0 ? log : null;
}

/** Détail de calcul normalisé d'un lancer, prêt pour l'affichage. */
export interface ThrowDetail {
  /** `false` quand le cœur ne livre pas encore `ThrowResult.breakdown`. */
  exact: boolean;
  baseMaster: number;
  baseBasic: number;
  flat: number;
  DL: number;
  DC: number;
  DS: number;
  MP: number;
  MT: number;
  affixMult: number;
  total: number;
  critAdditive: number[];
  critMultipliers: number[];
  /**
   * Multiplicateurs d'effets de sorts (Π m_S(a)), sorts eux-mêmes exclus :
   * `[puissance des sorts]` (le second facteur, `1 + préparation`, a disparu
   * avec le compteur : A100-2).
   */
  spellEffectMultipliers: number[];
  /** Diviseur du critique de l'ennemi — 1 sans ennemi `critDivide` (A27). */
  critDivisor: number;
  /** Diviseur des dés Basiques — 1 sans Tortue Épineuse Gigantesque (A31). */
  basicDivisor: number;
  /** Mage Maudit (A100-1) : la part de bonus des sorts est divisée par 2. */
  spellsHalved: boolean;
  /** Facteur global de *Préparation* (A100-2) : 1 sans effet (ou sans cœur A100). */
  prepMult: number;
  /** Emplacements retirés du lancer par l'ennemi (Orc Mage — §F). */
  blockedSlots: number;
  /**
   * *Tractopelle* (A72-17) : dés Basiques supplémentaires du lancer et somme
   * brute de leurs faces (déjà comprise dans `baseBasic`) ; 0 sinon.
   */
  backhoeDice: number;
  backhoeBasic: number;
  /** Niveaux des dés (A94) : bonus déjà compris dans `DL` (0 sans dé de niveau). */
  dieLevelBonus: number;
  /** Traces des Grimoires et des Matériaux (A95) ; neutres (1 ou 0) sans le cœur. */
  trace: MaterialTrace;
  /** Effets de l'A98 sur le lancer ; neutres (faux, 0, 1) sans le cœur. */
  a98: A98Trace;
  /** A122 : *Électrification* (Silicium 5) — facteur du produit et cumuls du lancer (1 et 0 sans effet). */
  electrifyMult: number;
  electrifyStacks: number;
  /** A122 : bonus d'*ArchiDicer* (1 + 33 % par sort, plafonné) — 1 sans effet. */
  archidicerMult: number;
  /** A123-3 : facteur du *Croupier* (×0,01 ou ×4) — 1 sans Croupier. */
  croupierMult: number;
  /** A123-2 : l'application de ce lancer a doublé le poison de la cible (*Andarielle*). */
  poisonDoubled: boolean;
  /**
   * Dégâts de la **marque de Null libérée** compris dans `total` (0 hors
   * libération : un lancer qui accumule n'ajoute rien à son propre total).
   */
  nullIncluded: number;
  /** *Triche* (A72-6) : lancer gardé (1 ou 2) et total écarté ; `null` sans Triche. */
  cheat: { kept: number; discardedTotal: number } | null;
  /** *Guinsoo* (A72-15) : coups du lancer (2 = dégâts appliqués deux fois). */
  hits: number;
  masterFaces: number[];
  basicFaces: number[];
  crit: boolean;
  /**
   * **Tous** les sorts déclenchés par le lancer (`ThrowResult.spells` — un sort
   * d'emplacement et un ou plusieurs sorts de dés peuvent tomber ensemble,
   * DECISIONS §E A9). Vide quand le lancer n'a rien déclenché.
   */
  spells: TriggeredSpell[];
  /**
   * Facteurs nommés que `ThrowDetail` ne connaît pas nommément (v0.7.0 :
   * Larbin, *Premiers*, *ENFER*, *Sorcellerie*…). Toujours affichés, même
   * inconnus — voir `ExtraFactor`.
   */
  extraFactors: ExtraFactor[];
}

/**
 * Ce que le lancer dit des Grimoires et des Matériaux (A95) : les facteurs
 * valent 1 et les reports 0 quand ils n'ont pas joué.
 */
export interface MaterialTrace {
  /** Grimoire des nombres : facteur global obtenu sur les sorts de dé (compris dans D_S). */
  grimoireNumbers: number;
  /** Face je gagne, Pile tu perds : facteur global obtenu sur les sorts d'emplacement (compris dans D_S). */
  grimoireFaces: number;
  /** Osseux : multiplicateur de l'ennemi brisé (entre dans le produit du total). */
  broken: number;
  /** Ferreux : facteur des dés Basiques (compris dans D_L). */
  ironBasic: number;
  /** Boisé 5 : le contenant a pris le maximum du lancer précédent. */
  woodCarry: number;
  /** Du Vide 5 : le lancer a pris le maximum du lancer précédent. */
  voidCarry: number;
  /** Silicium 3 : excédent reporté du lancer précédent, ajouté aux dégâts finaux. */
  overkillCarry: number;
}

/**
 * Ce que le lancer dit des nouveautés de l'A98, toutes neutres sans le cœur :
 * Basiques doublés (Vingt-dicte), dégâts retenus par la marque de Null (et ce
 * qu'un lancer libère, si le cœur le publie), diviseur d'Armure, part Mult_Corrupted des
 * dés Corrupted.
 */
export interface A98Trace {
  basicDoubled: boolean;
  nullHeld: number;
  nullReleased: number;
  armorDivisor: number;
  corruptedBonus: number;
}

const NEUTRAL_A98: A98Trace = { basicDoubled: false, nullHeld: 0, nullReleased: 0, armorDivisor: 1, corruptedBonus: 0 };

/** Traces A98 d'un `breakdown` (valeurs neutres si le cœur ne les publie pas). */
export function a98TraceOf(b: Record<string, unknown> | undefined): A98Trace {
  if (!b) return NEUTRAL_A98;
  return {
    basicDoubled: b.basicDoubled === true,
    nullHeld: Math.max(0, num(b.nullHeld, 0)),
    nullReleased: Math.max(0, num(b.nullReleased, 0)),
    armorDivisor: Math.max(1, num(b.armorDivisor, 1)),
    corruptedBonus: Math.max(0, num(b.corruptedBonus, 0)),
  };
}

const NEUTRAL_TRACE: MaterialTrace = {
  grimoireNumbers: 1,
  grimoireFaces: 1,
  broken: 1,
  ironBasic: 1,
  woodCarry: 0,
  voidCarry: 0,
  overkillCarry: 0,
};

/** Traces A95 d'un `breakdown` (valeurs neutres si le cœur ne les publie pas). */
export function materialTraceOf(b: Record<string, unknown> | undefined): MaterialTrace {
  if (!b) return NEUTRAL_TRACE;
  return {
    grimoireNumbers: num(b.grimoireNumbersMult, 1),
    grimoireFaces: num(b.grimoireFacesMult, 1),
    broken: num(b.brokenMult, 1),
    ironBasic: num(b.ironBasicMult, 1),
    woodCarry: Math.max(0, num(b.woodBasicCarry, 0)),
    voidCarry: Math.max(0, num(b.voidCarry, 0)),
    overkillCarry: Math.max(0, num(b.overkillCarry, 0)),
  };
}

function faceList(dice: readonly DieResult[], master: boolean): number[] {
  return dice.filter((d) => d.isMaster === master && !d.absorbed).map((d) => d.face);
}

/**
 * Normalise un lancer en détail de calcul (COMMENTS §1.2.5).
 *
 * Avec `breakdown`, tout vient du cœur. Sans lui, on reconstruit ce qui est
 * lisible depuis `ThrowResult` (`subtotal`, `flatBonus`, `critMult`,
 * `spellMult`, `containerMult`) et `exact` passe à `false` : l'écran affiche
 * alors la même structure, avec une note indiquant l'approximation.
 */
export function throwDetail(result: ThrowResult): ThrowDetail {
  const r = result as ExtendedThrow;
  const dice = Array.isArray(r.dice) ? r.dice : [];
  const masterFaces = faceList(dice, true);
  const basicFaces = faceList(dice, false);
  const b = r.breakdown;
  const crit = r.crit === true;
  // v0.5.0 : `ThrowResult.spells` (liste). Repli sur `ThrowResult.spell`
  // (v0.4.x, un seul sort d'emplacement) — voir `ui/spellsApi.ts`.
  const spells = mergeBreakdownSpells(triggeredSpells(r), b?.spells);

  if (b && typeof b === 'object') {
    const baseMaster = num(b.baseMaster);
    const baseBasic = num(b.baseBasic);
    const flat = num(b.flat);
    return {
      exact: true,
      baseMaster,
      baseBasic,
      flat,
      DL: num(b.DL, baseMaster + baseBasic + flat),
      DC: num(b.DC, 1),
      DS: num(b.DS, 1),
      MP: num(b.MP, 1),
      MT: num(b.MT, 1),
      affixMult: num(b.affixMult, 1),
      total: num(b.total, num(r.total)),
      critAdditive: numList(b.critAdditive),
      critMultipliers: numList(b.critMultipliers),
      spellEffectMultipliers: numList(b.spellEffectMultipliers),
      critDivisor: num(b.critDivisor, 1),
      basicDivisor: num(b.basicDivisor, 1),
      spellsHalved: b.spellsHalved === true,
      prepMult: Math.max(1, num(b.prepMult, 1)),
      blockedSlots: num(b.blockedSlots, 0),
      ...extrasOf(r, b as Record<string, unknown>),
      trace: materialTraceOf(b as Record<string, unknown>),
      a98: a98TraceOf(b as Record<string, unknown>),
      ...a122Of(r, b as Record<string, unknown>),
      masterFaces,
      basicFaces,
      crit,
      spells,
      extraFactors: collectExtraFactors(b as Record<string, unknown>),
    };
  }

  // ── Repli : reconstruction depuis les champs historiques ─────────────
  const flat = num(r.flatBonus);
  const masterSum = masterFaces.reduce((s, f) => s + f, 0);
  const basicSum = basicFaces.reduce((s, f) => s + f, 0);
  const DL = num(r.subtotal, masterSum + basicSum + flat);
  const spellProduct = spells.reduce((m, sp) => m * (sp.multiplier || 1), 1);
  return {
    exact: false,
    baseMaster: masterSum,
    baseBasic: basicSum,
    flat,
    DL,
    DC: crit ? num(r.critMult, 1) : 1,
    DS: spells.length > 0 ? num(r.spellMult, spellProduct) : 1,
    MP: 1,
    MT: 1,
    affixMult: 1,
    total: num(r.total),
    critAdditive: [],
    critMultipliers: [],
    spellEffectMultipliers: [],
    critDivisor: 1,
    basicDivisor: 1,
    spellsHalved: false,
    prepMult: 1,
    blockedSlots: 0,
    ...extrasOf(r, {}),
    trace: NEUTRAL_TRACE,
    a98: NEUTRAL_A98,
    ...a122Of(r, {}),
    masterFaces,
    basicFaces,
    crit,
    spells,
    extraFactors: [],
  };
}

/** Facteurs de l'A122 d'un lancer (neutres sans le cœur) et part de la marque de Null comprise dans le total. */
function a122Of(
  r: ExtendedThrow,
  b: Record<string, unknown>,
): Pick<ThrowDetail, 'electrifyMult' | 'electrifyStacks' | 'archidicerMult' | 'croupierMult' | 'poisonDoubled' | 'nullIncluded'> {
  const released = (r as unknown as { nullRelease?: unknown }).nullRelease !== undefined;
  return {
    electrifyMult: num(b.electrifyMult, 1),
    electrifyStacks: Math.max(0, num(b.electrifyStacks, 0)),
    archidicerMult: num(b.archidicerMult, 1),
    croupierMult: num(b.croupierMult, 1),
    poisonDoubled: (r as unknown as { poisonDoubled?: unknown }).poisonDoubled === true,
    // `breakdown.nullHeld` est la part libérée quand le lancer libère ; sinon c'est le cumul posté après coup (hors total).
    nullIncluded: released ? Math.max(0, num(b.nullHeld, 0)) : Math.max(0, num(b.nullReleased, 0)),
  };
}

/**
 * Fusionne les marqueurs *Arrière-garde* de `breakdown.spells[]` (A89) dans les
 * sorts de `ThrowResult.spells` (même identifiant) : le cœur peut les publier
 * dans l'un ou l'autre.
 */
function mergeBreakdownSpells(spells: TriggeredSpell[], raw: unknown): TriggeredSpell[] {
  if (!Array.isArray(raw)) return spells;
  return spells.map((sp) => {
    const found = raw.find((e) => e && typeof e === 'object' && (e as Record<string, unknown>).id === sp.id);
    return found ? withPrep(withRearguard(sp, found as Record<string, unknown>), found as Record<string, unknown>) : sp;
  });
}

/** Champs v0.26.0 d'un lancer (tractopelle, Triche, Guinsoo), lus avec tolérance. */
function extrasOf(
  r: ExtendedThrow,
  b: Record<string, unknown>,
): Pick<ThrowDetail, 'backhoeDice' | 'backhoeBasic' | 'dieLevelBonus' | 'cheat' | 'hits'> {
  const raw = r as unknown as { backhoe?: unknown; cheat?: unknown; hits?: unknown };
  const cheat = raw.cheat && typeof raw.cheat === 'object' ? (raw.cheat as Record<string, unknown>) : null;
  return {
    backhoeDice: num(raw.backhoe, 0),
    backhoeBasic: num(b.backhoeBasic, 0),
    dieLevelBonus: Math.max(0, num(b.dieLevelBonus, 0)),
    cheat: cheat ? { kept: num(cheat.kept, 1), discardedTotal: num(cheat.discardedTotal, 0) } : null,
    hits: num(raw.hits, 1),
  };
}

// ────────────────────────────────────────────────── Calculateur de dégâts

/** Retour de `game.expectedThrow()` (COMMENTS §7 — calculateur de debug). */
export interface ExpectedThrow {
  meanNoSpell: number;
  min: number;
  max: number;
  critChance: number;
  critMultiplier: number;
  meanWithCrit: number;
  /**
   * Probabilité de déclenchement par sort, **clés libres** : la v0.5.0 en
   * ajoute (sorts de dés) et en retire (Arc électrique, Sens de la vie). Le
   * calculateur affiche ce que le cœur renvoie, sans liste en dur.
   */
  spellChance: Record<string, number>;
  meanWithSpells: number;
  perThrowSeconds: number;
  dps: number;
  /**
   * Tout le reste de ce que renvoie `expectedThrow()` : les grandeurs que la
   * v0.7.0 ajoute (multiplicateur du Larbin, de *Premiers*, d'*ENFER*,
   * de *Sorcellerie*…) et celles qui viendront. Le calculateur les affiche
   * sous leur libellé connu, ou sous leur nom brut — jamais rien de muet.
   */
  extra: Record<string, number>;
}

/** Clés de `expectedThrow()` déjà portées nommément par `ExpectedThrow`. */
const KNOWN_EXPECTED_KEYS = new Set([
  'meanNoSpell',
  'min',
  'max',
  'critChance',
  'critMultiplier',
  'meanWithCrit',
  'spellChance',
  'meanWithSpells',
  'perThrowSeconds',
  'dps',
]);

/** `game.expectedThrow()`, ou `null` tant que le cœur ne l'expose pas. */
export function expectedThrow(game: Game): ExpectedThrow | null {
  const fn = (game as unknown as { expectedThrow?: () => unknown }).expectedThrow;
  if (typeof fn !== 'function') return null;
  let out: unknown;
  try {
    out = fn.call(game);
  } catch {
    return null;
  }
  if (!out || typeof out !== 'object') return null;
  const o = out as Record<string, unknown>;
  const chances = (o.spellChance && typeof o.spellChance === 'object'
    ? (o.spellChance as Record<string, unknown>)
    : {}) as Record<string, unknown>;
  const spellChance: Record<string, number> = {};
  for (const [id, value] of Object.entries(chances)) {
    if (typeof value === 'number' && Number.isFinite(value)) spellChance[id] = value;
  }
  const extra: Record<string, number> = {};
  for (const [key, value] of Object.entries(o)) {
    if (KNOWN_EXPECTED_KEYS.has(key)) continue;
    if (typeof value === 'number' && Number.isFinite(value)) extra[key] = value;
  }
  return {
    meanNoSpell: num(o.meanNoSpell),
    min: num(o.min),
    max: num(o.max),
    critChance: num(o.critChance),
    critMultiplier: num(o.critMultiplier, 1),
    meanWithCrit: num(o.meanWithCrit),
    spellChance,
    meanWithSpells: num(o.meanWithSpells),
    perThrowSeconds: num(o.perThrowSeconds),
    dps: num(o.dps),
    extra,
  };
}

// ───────────────────────────────────────────────────────── « Mur suivant »

type EnemyHpAt = (level: number, day: number, typeId: string) => number;

/**
 * `enemyHpAt(level, day, typeId)` — l'agent `src/core` l'exporte depuis
 * `core/enemies` **ou** `core/economy` : les deux modules sont inspectés à
 * l'exécution (import d'espace de noms, aucune liaison statique sur un nom qui
 * n'existe pas encore).
 */
function resolveEnemyHpAt(): EnemyHpAt | null {
  for (const mod of [ENEMIES_MODULE, ECONOMY_MODULE] as unknown as Record<string, unknown>[]) {
    const fn = mod.enemyHpAt;
    if (typeof fn === 'function') return fn as EnemyHpAt;
  }
  return null;
}

/** Méthode `game.enemyHpAt` de la façade (surcharges de debug comprises). */
function gameEnemyHpAt(game: Game): EnemyHpAt | null {
  const fn = (game as unknown as { enemyHpAt?: unknown }).enemyHpAt;
  return typeof fn === 'function' ? (fn as EnemyHpAt).bind(game) : null;
}

/** Le cœur expose-t-il `enemyHpAt` ? (sinon : repli `enemyHp × waveOf`). */
export function hasEnemyHpAt(game?: Game): boolean {
  if (game && gameEnemyHpAt(game)) return true;
  return resolveEnemyHpAt() !== null;
}

/**
 * PV d'un ennemi au niveau `level`, jour `day`. Repli sur les formules déjà
 * publiques de `core/economy` (`enemyHp` + `waveOf`), surcharges de debug
 * comprises quand `game.getOverrides()` existe.
 */
export function enemyHpAt(game: Game, level: number, day: number, typeId: string): number {
  // La méthode de façade est préférée : elle applique les surcharges de debug.
  const direct = gameEnemyHpAt(game) ?? resolveEnemyHpAt();
  if (direct) {
    try {
      const n = direct(level, day, typeId);
      if (Number.isFinite(n)) return n;
    } catch {
      /* repli ci-dessous */
    }
  }
  const def = ENEMY_BY_ID[typeId as keyof typeof ENEMY_BY_ID];
  if (!def) return 0;
  const eco = ECONOMY_MODULE as unknown as {
    enemyHp?: (base: number, wave: number, bal?: unknown) => number;
    waveOf?: (level: number, day: number) => number;
  };
  if (typeof eco.enemyHp !== 'function' || typeof eco.waveOf !== 'function') return def.baseHp;
  const wave = eco.waveOf(level, day);
  const getOverrides = (game as unknown as { getOverrides?: () => unknown }).getOverrides;
  let bal: unknown;
  try {
    const raw = typeof getOverrides === 'function' ? getOverrides.call(game) : {};
    const resolve = (
      BALANCE_MODULE as unknown as { resolveBalance?: (o: unknown) => unknown }
    ).resolveBalance;
    bal = typeof resolve === 'function' ? resolve(raw) : undefined;
  } catch {
    bal = undefined;
  }
  try {
    return bal === undefined ? eco.enemyHp(def.baseHp, wave) : eco.enemyHp(def.baseHp, wave, bal);
  } catch {
    return def.baseHp;
  }
}

export interface NextWall {
  level: number;
  /** PV moyen d'un ennemi ordinaire de l'Invasion (pool pondéré). */
  meanEnemyHp: number;
  /** Nombre d'ennemis ordinaires avant le boss. */
  enemyCount: number;
  bossHp: number;
  totalHp: number;
  /** Durée de l'Invasion (s), stats dérivées du joueur. */
  duration: number;
  /** DPS nécessaire = totalHp / duration. */
  requiredDps: number;
}

/**
 * « Mur suivant » : les 4 ennemis moyens + le boss de la prochaine Invasion au
 * niveau courant. Le PV moyen est la moyenne **pondérée par les poids
 * d'apparition** du pool de la vague (`spawnPool`), boss exclu.
 */
export function nextWall(game: Game): NextWall {
  const level = Math.max(1, num(game.state.level, 1));
  const day = BALANCE.invasionDay;
  const count = Math.max(0, BALANCE.invasionEnemyCount - 1);

  const pool = (
    ENEMIES_MODULE as unknown as {
      spawnPool?: (wave: number, world?: unknown) => { id: string; weight: number }[];
    }
  ).spawnPool;
  const eco = ECONOMY_MODULE as unknown as { waveOf?: (l: number, d: number) => number };
  let mean = 0;
  let defs: { id: string; weight: number }[] = [];
  if (typeof pool === 'function' && typeof eco.waveOf === 'function') {
    try {
      // Mode infini (phase 5) : le pool du monde tiré, boss ordinaires compris.
      defs = pool(eco.waveOf(level, day), game.state.world);
    } catch {
      defs = [];
    }
  }
  if (defs.length === 0) {
    // Repli : les ennemis ordinaires de la zone du niveau (§F, poids égaux) —
    // jamais l'ancien 'skeleton' en dur, qui n'a plus de sens hors zone 2.
    defs = zoneForLevel(level)
      .enemies.filter((e) => !e.boss)
      .map((e) => ({ id: e.id, weight: 1 }));
  }
  if (defs.length === 0) defs = [{ id: ZONES[0].bossId, weight: 1 }];
  const totalWeight = defs.reduce((s, d) => s + Math.max(0, d.weight), 0) || defs.length;
  for (const d of defs) {
    const w = Math.max(0, d.weight) || 1;
    mean += (enemyHpAt(game, level, day, d.id) * w) / totalWeight;
  }

  // Le boss de la prochaine Invasion : celui de la zone, ou le boss tiré du
  // niveau infini (phase 5).
  const bossId = currentBossId(game.state);
  const bossHp = enemyHpAt(game, level, day, bossId);
  const totalHp = mean * count + bossHp;
  const duration = num(game.getDerivedStats().invasionDuration, 0);
  return {
    level,
    meanEnemyHp: mean,
    enemyCount: count,
    bossHp,
    totalHp,
    duration,
    requiredDps: duration > 0 ? totalHp / duration : Number.POSITIVE_INFINITY,
  };
}

// ───────────────────────────────────────── Ligne d'effet de l'ennemi (v0.8.0)

/** « 15 % » — repli local si `data/enemies.effectLabel` ne pouvait être importée. */
function pct(value: number): string {
  return `${Math.round(value * 100)} %`;
}

/** « un », « deux », « trois » — un nombre de dés lisible ; au-delà, le chiffre. */
function countWord(value: unknown): string {
  const n = typeof value === 'number' && Number.isFinite(value) ? Math.max(1, Math.floor(value)) : 1;
  return ['', 'un', 'deux', 'trois', 'quatre'][n] || String(n);
}

/** Liste vide partagée (aucune allocation sans effet). */
const NO_LINES: readonly string[] = Object.freeze([]);

/**
 * Table de repli, **par `kind`**, au cas où `data/enemies.ts` ne livrerait pas
 * encore `effectLabel` (COMMENTS v0.7.0 §3 / §F). `effectLabel` est appelé en
 * priorité : cette table n'existe que pour ne jamais laisser le HUD muet.
 */
function fallbackEffectLabel(effect: EnemyEffect): string {
  // A98 : les genres ajoutés par le cœur (absents du type tant qu'il n'est pas fusionné).
  const extra = effect as unknown as Record<string, unknown>;
  const pctOf = (v: unknown): string => `${Math.round((typeof v === 'number' ? v : 0) * 100)} %`;
  switch (effect.kind as string) {
    case 'halveBasicChance':
      return `${pctOf(extra.chance)} de diviser par ${extra.divisor ?? 2} les dégâts de vos dés Basiques`;
    case 'stunOnHit':
      return `${pctOf(extra.chance)} de vous assommer ${String(extra.seconds ?? '').replace('.', ',')} s à chaque lancer`;
    case 'voidDice':
      return `Annule au hasard ${countWord(extra.count)} de vos dés Maîtres à chaque lancer`;
    case 'halveSpells':
      // A100-1 (Mage Maudit) : la part de bonus des sorts est divisée.
      return `Divise par ${extra.divisor ?? 2} les effets des sorts`;
    case 'blockSlots':
      return `Bloque ${extra.slots} emplacement${Number(extra.slots) > 1 ? 's' : ''} de dé Maître`;
    case 'armor':
      return `Réduit les dégâts du lancer par ${String(extra.perHit ?? 1.05).replace('.', ',')} par coup reçu, ${extra.maxStacks ?? 5} fois`;
    case 'timeTick':
      return `Retire ${extra.seconds ?? 1} s au chrono toutes les ${extra.every ?? 3} s`;
  }
  switch (effect.kind) {
    case 'revive':
      return `Revit avec ${pct(effect.percent)} de ses PV`;
    case 'dodge':
      return `Esquive ${pct(effect.chance)} des lancers`;
    case 'regen':
      return `Régénère ${pct(effect.percentPerSecond)} de sa vie par seconde`;
    case 'shield':
      return 'Annule les dégâts du premier dé Maître';
    case 'timeDrain':
      return "Réduit le temps d'expédition par intermittence";
    case 'slowThrow':
      return `Réduit votre vitesse de lancer de ${pct(effect.percent)}`;
    case 'critDivide':
      return `Divise les dégâts critiques par ${effect.divisor}`;
    case 'stun':
      return 'Peut vous assommer';
    case 'explodeFaster':
      return 'Explose en mourant : le chrono accélère';
    case 'engineer':
      return 'Effet aléatoire à sa mort';
    case 'hitLimit':
      return `Doit mourir en ${effect.maxHits} coups, sinon temps restant ÷ 2`;
    case 'halveBasic':
      return `Divise par ${effect.divisor} les dégâts de vos dés Basiques`;
    default:
      return '';
  }
}

/**
 * Libellé de l'effet de l'ennemi affiché sous son nom (COMMENTS v0.7.0 §3) :
 * `effectLabel()` de `src/data/enemies.ts` si livrée, sinon la table locale
 * ci-dessus. `null` si l'ennemi n'a pas d'effet, pour que le HUD masque
 * entièrement la ligne plutôt que d'afficher un vide.
 *
 * Serpent (`hitLimit`) : le compteur de coups est dessiné par la vue de combat
 * sous la barre de PV de l'ennemi (« coups 1/3 »), pas ici — A28.
 */
export function enemyEffectLine(game: Game): string | null {
  return enemyEffectLines(game)[0] ?? null;
}

/** Libellé d'un effet seul : données du cœur, sinon la table locale (jamais d'erreur). */
function labelOfEffect(effect: EnemyEffect): string {
  let label = '';
  try {
    label = dataEffectLabel(effect) || '';
  } catch {
    label = '';
  }
  return label || fallbackEffectLabel(effect);
}

/**
 * Effets de l'ennemi courant : `enemy.effects` (liste, A98 — un boss peut en
 * porter plusieurs), sinon l'ancien `enemy.effect` seul. Tableau vide sans
 * ennemi ni effet.
 */
function enemyEffectsOf(game: Game): EnemyEffect[] {
  const enemy = game.state.enemy as unknown as { effects?: unknown; effect?: unknown } | null;
  if (!enemy) return [];
  if (Array.isArray(enemy.effects)) return enemy.effects.filter((e): e is EnemyEffect => !!e && typeof e === 'object');
  return enemy.effect && typeof enemy.effect === 'object' ? [enemy.effect as EnemyEffect] : [];
}

/** Dernière lecture : la bannière appelle `enemyEffectLines` à chaque image, rien ne se recalcule sans changement. */
let linesCache: { enemy: unknown; list: unknown; size: number; first: unknown; lines: readonly string[] } | null = null;

/**
 * **Une ligne par effet** de l'ennemi courant, pour sa bannière (A98) :
 * `effectLabels(enemy.effects)` de `data/enemies.ts` (livré par le cœur A98),
 * sinon un libellé par effet via `effectLabel` et la table locale. Vide sans
 * effet (la bannière n'écrit alors rien). Le résultat est **mis en cache** tant
 * que l'ennemi et sa liste d'effets ne changent pas : même tableau d'une image
 * à l'autre.
 */
export function enemyEffectLines(game: Game): readonly string[] {
  const enemy = game.state.enemy as unknown as { effects?: unknown; effect?: unknown } | null;
  if (!enemy) return NO_LINES;
  const list = Array.isArray(enemy.effects) ? enemy.effects : enemy.effect;
  const size = Array.isArray(list) ? list.length : list ? 1 : 0;
  const first = Array.isArray(list) ? list[0] : list;
  const c = linesCache;
  if (c && c.enemy === enemy && c.list === list && c.size === size && c.first === first) return c.lines;

  const effects = enemyEffectsOf(game);
  let lines: string[] = [];
  const many = (ENEMY_DATA_MODULE as unknown as { effectLabels?: (e: EnemyEffect[]) => unknown }).effectLabels;
  if (typeof many === 'function' && effects.length > 0) {
    try {
      const out = many(effects);
      if (Array.isArray(out)) lines = out.filter((l): l is string => typeof l === 'string' && l.length > 0);
    } catch {
      lines = [];
    }
  }
  if (lines.length === 0) lines = effects.map(labelOfEffect).filter((l) => l.length > 0);
  const result: readonly string[] = lines.length > 0 ? lines : NO_LINES;
  linesCache = { enemy, list, size, first, lines: result };
  return result;
}

// ─────────────────────────────────── Raison de perte d'Expédition (Serpent)

/**
 * Dernière raison de perte d'**Expédition** capturée sur l'événement
 * `expeditionLost` (A28 : Serpent survivant à son 3e coup ; depuis la v0.52.0,
 * A102, le Serpent divise le temps restant par 2 et plus aucun monstre
 * n'émet l'événement — gardé pour une cause future). Vit au niveau du module : l'écran `expeditionEnd` est
 * monté **après** que l'événement s'est produit pendant le combat, il ne peut
 * donc pas s'y abonner lui-même.
 */
let lastExpeditionLossReason: string | null = null;

/**
 * S'abonne une fois pour toutes (depuis `ui/app.ts`) à `expeditionLost` pour
 * mémoriser une phrase lisible. Sans le champ `enemy.effect.maxHits` (cœur pas
 * encore livré, ou cause future sans Serpent), la raison reste `null` et
 * l'écran retombe sur un message générique.
 */
export function trackExpeditionLoss(game: Game): () => void {
  const handler = (payload: unknown): void => {
    const enemy = (payload as { enemy?: { name?: unknown; effect?: unknown } } | undefined)?.enemy;
    const name = typeof enemy?.name === 'string' ? enemy.name : null;
    const effect = enemy?.effect as { kind?: string; maxHits?: unknown } | undefined;
    const maxHits = typeof effect?.maxHits === 'number' ? effect.maxHits : null;
    lastExpeditionLossReason =
      name && maxHits ? `Le ${name} a survécu à ${maxHits} coup${maxHits > 1 ? 's' : ''}.` : name;
  };
  return onGameEvent(game, 'expeditionLost', handler as unknown as () => void);
}

/** Raison de la dernière Expédition perdue (`null` si inconnue ou aucune). */
export function expeditionLossReason(): string | null {
  return lastExpeditionLossReason;
}
